import { useMemo } from "react";
import { Video as VideoIcon, ExternalLink } from "lucide-react";

/**
 * Detects supported video platforms and returns an embeddable iframe URL.
 * Supported: YouTube (watch/short/youtu.be/embed), Vimeo, Rumble.
 * Anything else falls back to a plain "Open link" card.
 */
export function detectVideo(url) {
  if (!url || typeof url !== "string") return null;
  const raw = url.trim();
  if (!raw) return null;
  let u;
  try { u = new URL(raw); } catch { return null; }
  const host = u.hostname.replace(/^www\./, "").toLowerCase();

  // YouTube
  if (host === "youtu.be") {
    const id = u.pathname.slice(1).split("/")[0];
    if (id) return { platform: "YouTube", embed: `https://www.youtube.com/embed/${id}`, url: raw };
  }
  if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
    if (u.pathname === "/watch") {
      const id = u.searchParams.get("v");
      if (id) return { platform: "YouTube", embed: `https://www.youtube.com/embed/${id}`, url: raw };
    }
    const m = u.pathname.match(/^\/(embed|shorts|live)\/([^/?#]+)/);
    if (m) return { platform: "YouTube", embed: `https://www.youtube.com/embed/${m[2]}`, url: raw };
  }

  // Vimeo
  if (host === "vimeo.com" || host.endsWith(".vimeo.com")) {
    // Handle /video/ID, /ID, or /channels/staff/ID
    const parts = u.pathname.split("/").filter(Boolean);
    const id = parts.find((p) => /^\d+$/.test(p));
    if (id) return { platform: "Vimeo", embed: `https://player.vimeo.com/video/${id}`, url: raw };
  }
  if (host === "player.vimeo.com") {
    return { platform: "Vimeo", embed: raw, url: raw };
  }

  // Rumble — user usually pastes rumble.com/vXXXXXX-title.html
  // Rumble does not offer a public URL-based embed builder like YouTube, but the
  // /embed/{id}/ path works when you know the numeric embed id. Since users paste
  // the share URL, we fall back to the "Open on Rumble" card unless they paste
  // an embed URL directly.
  if (host === "rumble.com" || host.endsWith(".rumble.com")) {
    if (u.pathname.startsWith("/embed/")) {
      return { platform: "Rumble", embed: raw, url: raw };
    }
    return { platform: "Rumble", embed: null, url: raw };
  }

  return null;
}

export default function VideoEmbed({ url, className = "" }) {
  const info = useMemo(() => detectVideo(url), [url]);
  if (!info) {
    // Not a recognized video platform — render nothing so we don't clutter the topic
    return null;
  }
  if (info.embed) {
    return (
      <div className={`mt-4 ${className}`} data-testid="topic-video-embed">
        <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2 flex items-center gap-1.5">
          <VideoIcon className="w-3 h-3 text-[#0D9488]" />
          <span>// {info.platform}</span>
        </div>
        <div className="border border-slate-200 bg-black overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
          <iframe
            src={info.embed}
            title={info.platform}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="w-full h-full"
          />
        </div>
      </div>
    );
  }
  // Fallback card: Rumble or any recognized platform without direct embed
  return (
    <a
      href={info.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`mt-4 inline-flex items-center gap-2 border border-slate-200 hover:border-[#0D9488] bg-slate-50 hover:bg-slate-50 px-3 h-10 transition-colors text-slate-800 ${className}`}
      data-testid="topic-video-link"
    >
      <VideoIcon className="w-4 h-4 text-[#0D9488]" />
      <span className="font-mono-num text-[11px] uppercase tracking-widest">Open on {info.platform}</span>
      <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
    </a>
  );
}
